import { Checkbox, toast } from '@systemeio/ui'
import Option from 'modules/products/components/Options/option'
import ProductErrorsInterface from 'modules/products/types/error-response-content-interface'
import {
  ProductOptionInterface,
  ProductOptionValueInterface,
} from 'modules/products/types/product-option-interface'
import { getVariants } from 'modules/products/utils/product-variants-utils'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { ulid } from 'ulid'
import { PlusIcon } from '@systemeio/ui/icons'

interface OptionsProps {
  options: ProductOptionInterface[]
  setOptions: React.Dispatch<React.SetStateAction<ProductOptionInterface[]>>
  errors: Record<string, Record<string, string[]>>
  setErrors: React.Dispatch<React.SetStateAction<ProductErrorsInterface>>
  maxProductVariants: number
}

function Options({ options, setOptions, errors, setErrors, maxProductVariants }: OptionsProps) {
  const { t } = useLocoTranslation()
  const [variantOverloadError, setVariantOverloadError] = useState('')
  const [isChecked, setIsChecked] = useState(!!options.length)
  const [toastShown, setToastShown] = useState(false)

  const handleOnChangeCheckbox = (checked: boolean) => {
    if (checked) {
      handleAddOption()
    } else {
      setOptions([])
    }
    setIsChecked(checked)
  }

  const handleAddOption = () => {
    const newOption = {
      id: ulid(),
      name: '',
      optionValues: [] as ProductOptionValueInterface[],
    }
    const newOptionValue = {
      id: ulid(),
      value: '',
    }
    newOption.optionValues.push(newOptionValue)
    setOptions(prev => [...prev, newOption])
  }

  const handleChangeOptionName = (optionId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setOptions(prev => prev.map(o => (o.id === optionId ? { ...o, name: e.target.value } : o)))
    setErrors(prev => {
      const oldState = { ...prev }
      if (Object.keys(oldState.options).includes(optionId)) {
        delete oldState.options[optionId]
      }
      return oldState
    })
  }

  const handleChangeOptionValue =
    (optionId: string) => (e: React.ChangeEvent<HTMLInputElement>, optionValueId: string) => {
      setVariantOverloadError('')
      const targetOption = options.find(o => o.id === optionId)
      if (targetOption) {
        const filteredOptionValues = targetOption.optionValues
          .map(o => {
            return o.id === optionValueId ? { ...o, value: e.target.value } : o
          })
          .filter(o => !!o.value)

        const updatedOption = {
          ...targetOption,
          optionValues:
            filteredOptionValues.length > 0
              ? filteredOptionValues
              : [
                  {
                    id: ulid(),
                    value: '',
                  },
                ],
        }
        const variantsCount = getVariants(
          options.map(o => (o.id === optionId ? updatedOption : o)),
          [],
        ).length

        if (variantsCount <= maxProductVariants) {
          setOptions(prev => prev.map(o => (o.id === optionId ? updatedOption : o)))
          addEmptyOptionValue(updatedOption)
        } else if (!toastShown) {
          setToastShown(true)
          toast.error(
            t('dashboard.products.variants.overload.error_message', {
              numberOfVariants: maxProductVariants,
            }),
          )
        }
      }
    }

  const addEmptyOptionValue = (option: ProductOptionInterface) => {
    if (option.optionValues[option.optionValues.length - 1]?.value) {
      const newOptionValue = {
        id: ulid(),
        value: '',
      }
      const updatedOption = {
        ...option,
        optionValues: [...option.optionValues, newOptionValue],
      }
      setOptions(prev => prev.map(o => (o.id === option.id ? updatedOption : o)))
    }
  }

  const handleDeleteOption = (optionId: string) => () => {
    setOptions(prev => prev.filter(o => o.id !== optionId))
  }

  const handleDeleteOptionValue =
    (optionId: string) => (e: React.MouseEvent<SVGSVGElement>, optionValueId: string) => {
      setVariantOverloadError('')
      const targetOption = options.find(o => o.id === optionId)
      if (targetOption) {
        const filteredOptionValues = targetOption.optionValues.filter(o => o.id !== optionValueId)

        const updatedOption = {
          ...targetOption,
          optionValues: filteredOptionValues,
        }
        setOptions(prev => prev.map(o => (o.id === optionId ? updatedOption : o)))
      }
    }

  useEffect(() => {
    setIsChecked(!!options.length)
  }, [options])

  useEffect(() => {
    if (toastShown) {
      setTimeout(() => {
        setToastShown(false)
      }, 3000)
    }
  }, [toastShown])

  return (
    <>
      <div className="mt-4">
        <Checkbox
          value={isChecked}
          onChange={handleOnChangeCheckbox}
          label={t('dashboard.products.options.confirm')}
        />
      </div>
      <div className="my-2">
        {isChecked && (
          <hr className="border-text-secondary -ml-6 -mr-6 mb-2 mt-2 border-t-[1.5px] border-solid opacity-40" />
        )}
        {options.map(option => (
          <Option
            option={option}
            key={option.id}
            changeOptionName={handleChangeOptionName(option.id)}
            changeOptionValue={handleChangeOptionValue(option.id)}
            deleteOption={handleDeleteOption(option.id)}
            deleteOptionValue={handleDeleteOptionValue(option.id)}
            addEmptyOptionValue={addEmptyOptionValue}
            error={Object.keys(errors).includes(option.id) ? errors[option.id]?.name?.join('') : ''}
            errorOptionValues={
              Object.keys(errors).includes(option.id)
                ? errors[option.id]?.optionValues?.join('')
                : ''
            }
          />
        ))}
      </div>
      {variantOverloadError && (
        <div className="text-danger flex items-center justify-center">{variantOverloadError}</div>
      )}
      {isChecked && (
        <button
          className={`text-brand-primary mt-4 flex cursor-pointer gap-1`}
          onClick={handleAddOption}
        >
          <PlusIcon fill={`rgb(var(--brand-primary))`} />
          {t('dashboard.products.options.add_options.title')}
        </button>
      )}
    </>
  )
}
export default Options
